import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { Sessao } from '../../auth';
import { dataHoraCurta } from '../../acesso';
import { Icone } from '../../design/Icone';
import { reais } from '../../produtos';
import { useValoresOcultos } from '../../design/sigilo';
import {
  corrigirLote,
  data,
  diaMes,
  lancarLote,
  lerProdutoComLotes,
  origem,
  rotuloDoEstado,
  comoFalha,
} from '../../validades';
import type { EventoDoLote, LoteExistente, ProdutoComLotes } from '../../validades';
import { mensagemDeFalha, useEscape } from '../equipe/Equipe';
import { Selo } from './PreVencidos';
import { linhaClicavel } from '../../design/linha-clicavel';

/**
 * TELA 25-B — PRODUTO COM OS LOTES, DETALHE DO LOTE E LANÇAMENTO.
 *
 * Cada lote com validade, saldo, fonte e estado. A soma dos lotes é comparada com o estoque do
 * produto e, se não fechar, a tela mostra os DOIS números e não ajusta nenhum. Lançar lote trata
 * duplicidade e conflito de validade sem escolher sozinho; corrigir exige motivo e fica na linha
 * do tempo do lote.
 */

const TEXTO_DA_REGRA_DO_L = 'Não inclua (L). Digite somente o código que aparece depois de (L).';

type Painel =
  | { readonly tipo: 'nada' }
  | { readonly tipo: 'lote'; readonly id: string }
  | { readonly tipo: 'lancar' }
  | { readonly tipo: 'corrigir'; readonly id: string };

export function DrawerDeLotes({
  productId,
  sessao,
  configura,
  aoFechar,
  aoMudar,
  iniciarLancando = false,
}: {
  productId: string;
  sessao: Sessao;
  configura: boolean;
  aoFechar: () => void;
  aoMudar: () => void;
  /** Veio do "Novo lote": a gaveta já abre no formulário de lançar lote. */
  iniciarLancando?: boolean;
}) {
  const ocultos = useValoresOcultos();
  const [p, setP] = useState<ProdutoComLotes | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [painel, setPainel] = useState<Painel>(
    iniciarLancando && configura ? { tipo: 'lancar' } : { tipo: 'nada' },
  );
  useEscape(aoFechar, true);

  const carregar = useCallback(async () => {
    const r = await lerProdutoComLotes(productId);
    if (!r.ok) return setFalha(mensagemDeFalha(r.falha));
    setFalha(null);
    setP(r.dados);
  }, [productId]);
  useEffect(() => {
    void carregar();
  }, [carregar]);

  const depoisDeSalvar = async (proximo: Painel) => {
    await carregar();
    aoMudar();
    setPainel(proximo);
  };

  const ativos = p?.lotes.filter((l) => l.ativo) ?? [];
  const inativos = p?.lotes.filter((l) => !l.ativo) ?? [];
  const selecionado =
    painel.tipo === 'lote' || painel.tipo === 'corrigir'
      ? p?.lotes.find((l) => l.id === painel.id)
      : undefined;

  return (
    <div className="ac-veu" onClick={aoFechar} role="presentation">
      <aside
        className="ac-drawer pv-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={p?.descricao ?? 'Lotes do produto'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ac-drawer-cabeca">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="ac-drawer-nome">{p?.descricao ?? 'Carregando…'}</div>
            {p && (
              <div className="pd-drawer-codigos">
                {[p.codigo, p.ean ? `EAN ${p.ean}` : null, p.fabricante, p.grupo]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
          </div>
          {p && ativos.length > 0 && (
            <div className="pv-selos-cabeca">
              <span className="pv-selo pv-selo-aviso">PRÉ-VENCIDO</span>
              <span className="pv-selo pv-selo-neutro">
                {ativos.length} {ativos.length === 1 ? 'LOTE' : 'LOTES'}
              </span>
            </div>
          )}
          <button type="button" className="ac-fechar" onClick={aoFechar} aria-label="Fechar">
            <Icone nome="x" tamanho={16} traco={1.8} />
          </button>
        </div>

        {falha && (
          <div className="ac-secao">
            <div className="ac-erro" role="alert">
              <div className="ac-erro-titulo">Não foi possível carregar os lotes</div>
              <div className="ac-erro-texto">{falha}</div>
            </div>
          </div>
        )}

        {p && (
          <>
            <div className="ac-secao pv-numeros">
              <Numero
                rotulo="ESTOQUE TOTAL"
                valor={p.estoqueTotal ?? '—'}
                sub={
                  p.estoqueAtualizadoEm
                    ? `relatório · ${dataHoraCurta(p.estoqueAtualizadoEm)}`
                    : 'sem relatório'
                }
              />
              <Numero
                rotulo="LOTES ATIVOS"
                valor={String(ativos.length)}
                sub={
                  inativos.length
                    ? `+${String(inativos.length)} fora (esgotado/expirado)`
                    : 'nenhum fora'
                }
              />
              <Numero
                rotulo="VENDER POR"
                valor={p.venderPor ? reais(p.venderPor) : 'sem base'}
                sub={p.pmc ? `PMC ${reais(p.pmc)}` : 'PMC indisponível'}
                alerta={!p.venderPor}
              />
              {!ocultos && (
                <Numero
                  rotulo="PREMIAÇÃO"
                  valor={p.premioBase ? reais(p.premioBase) : '—'}
                  sub={
                    p.venderPor
                      ? 'na base · regra vigente do servidor'
                      : 'sem base cadastrada paga R$ 0,00'
                  }
                />
              )}
            </div>

            <div className="ac-secao">
              <div className="ac-secao-cabeca">
                <div className="pd-opcao-titulo">Lotes e validades</div>
                <span className="ac-espaco" />
                <span className="ac-nota" style={{ fontSize: 11 }}>
                  {ativos.length} {ativos.length === 1 ? 'lote' : 'lotes'} · a urgência é por lote
                </span>
              </div>
              {p.lotes.length === 0 ? (
                <div className="ac-vazio" style={{ padding: 16 }}>
                  <div className="ac-vazio-titulo">Nenhum lote cadastrado neste produto</div>
                  <div className="ac-vazio-texto">
                    Lance o primeiro lote com a validade da embalagem.
                  </div>
                </div>
              ) : (
                <div className="ac-cartao">
                  <table className="ac-tabela pv-tabela-lotes">
                    <thead>
                      <tr>
                        <th>LOTE</th>
                        <th>VALIDADE</th>
                        <th className="ac-direita">DIAS</th>
                        <th className="ac-direita">QTD</th>
                        <th className="ac-col-larga">FONTE · ATUALIZADO</th>
                        <th>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.lotes.map((l) => {
                        const est = l.ativo
                          ? rotuloDoEstado(l.estado, l.quantidade === null)
                          : {
                              texto: l.motivoInativo === 'expirado' ? 'Expirado' : 'Esgotado',
                              tom: 'neutro' as const,
                            };
                        const dias = 'dias' in l.estado ? l.estado.dias : null;
                        return (
                          <tr
                            key={l.id}
                            className={`pv-linha-lote${selecionado?.id === l.id ? ' pv-linha-selecionada' : ''}${l.ativo ? '' : ' pv-linha-inativa'}`}
                            {...linhaClicavel(() => setPainel({ tipo: 'lote', id: l.id }))}
                          >
                            <td className="ac-mono">{l.codigo ?? '—'}</td>
                            <td className="ac-num">{data(l.validade)}</td>
                            <td className={`ac-direita pv-dias pv-dias-${est.tom}`}>
                              {dias === null ? '—' : String(dias).replace('-', '−')}
                            </td>
                            <td className="ac-direita ac-num">{l.quantidade ?? 'indisponível'}</td>
                            <td className="ac-cinza ac-col-larga">
                              {origem(l.origem)}
                              {l.origem === 'manual' && l.criadoPor
                                ? ` · ${l.criadoPor.split(' ')[0] ?? ''}`
                                : ''}{' '}
                              · {dataHoraCurta(l.atualizadoEm)}
                            </td>
                            <td>
                              <Selo texto={est.texto} tom={est.tom} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <Conferencia p={p} ativos={ativos.map((l) => l.quantidade)} />
              <div className="pv-acoes-lotes">
                {configura && (
                  <button
                    type="button"
                    className="ac-botao ac-botao-primario"
                    onClick={() => setPainel({ tipo: 'lancar' })}
                  >
                    <Icone nome="plus" tamanho={14} traco={1.8} />
                    Lançar novo lote
                  </button>
                )}
              </div>
            </div>

            {painel.tipo === 'lote' && selecionado && (
              <DetalheDoLote
                lote={selecionado}
                descricao={p.descricao}
                configura={configura}
                aoCorrigir={() => setPainel({ tipo: 'corrigir', id: selecionado.id })}
              />
            )}
            {painel.tipo === 'corrigir' && selecionado && (
              <Correcao
                sessao={sessao}
                lote={selecionado}
                aoCancelar={() => setPainel({ tipo: 'lote', id: selecionado.id })}
                aoSalvar={() => void depoisDeSalvar({ tipo: 'lote', id: selecionado.id })}
                aoRecarregar={() => void carregar()}
              />
            )}
            {painel.tipo === 'lancar' && (
              <Lancamento
                sessao={sessao}
                productId={p.productId}
                aoCancelar={() => setPainel({ tipo: 'nada' })}
                aoSalvar={(id) => void depoisDeSalvar({ tipo: 'lote', id })}
                aoCorrigirExistente={(id) => setPainel({ tipo: 'corrigir', id })}
              />
            )}
          </>
        )}
      </aside>
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  sub,
  alerta,
}: {
  rotulo: string;
  valor: string;
  sub: string;
  alerta?: boolean;
}) {
  return (
    <div className="pv-numero">
      <div className="pd-dado-rotulo">{rotulo}</div>
      <div className={`pv-numero-valor${alerta ? ' pd-indisponivel' : ''}`}>{valor}</div>
      <div className="pv-numero-sub">{sub}</div>
    </div>
  );
}

/** "3 + 8 + 14 = 25 unidades, igual ao estoque total" — ou os DOIS números, sem ajustar nenhum. */
function Conferencia({ p, ativos }: { p: ProdutoComLotes; ativos: readonly (string | null)[] }) {
  if (ativos.length === 0) return null;
  if (p.somaDosLotes === null)
    return (
      <div className="pv-conferencia">
        Algum lote está sem saldo informado: a soma dos lotes não é conhecida e o estoque total (
        {p.estoqueTotal ?? '—'}) não é rateado entre eles.
      </div>
    );
  const conta = `${ativos.join(' + ')} = ${p.somaDosLotes} unidades`;
  const igual = p.estoqueTotal !== null && Number(p.estoqueTotal) === Number(p.somaDosLotes);
  return (
    <div className="pv-conferencia">
      <b>{conta}</b>
      {igual
        ? ', igual ao estoque total.'
        : `, e o estoque total do produto é ${p.estoqueTotal ?? 'indisponível'}. A tela mostra os dois números e não ajusta nenhum deles.`}
    </div>
  );
}

function DetalheDoLote({
  lote,
  descricao,
  configura,
  aoCorrigir,
}: {
  lote: ProdutoComLotes['lotes'][number];
  descricao: string;
  configura: boolean;
  aoCorrigir: () => void;
}) {
  const dias = 'dias' in lote.estado ? lote.estado.dias : null;
  return (
    <div className="ac-secao pv-detalhe">
      <div className="pv-detalhe-cabeca">
        <div>
          <div className="pv-detalhe-titulo">Lote {lote.codigo ?? 'sem código'}</div>
          <div className="ac-subtitulo">{descricao}</div>
        </div>
        {dias !== null && lote.ativo && (
          <span className={`pv-selo pv-selo-${dias < 0 ? 'erro' : dias <= 30 ? 'aviso' : 'ok'}`}>
            {dias < 0 ? `VENCIDO HÁ ${String(-dias)} DIAS` : `${String(dias)} DIAS`}
          </span>
        )}
      </div>
      <div className="pv-detalhe-grade">
        <Dado rotulo="Validade" valor={data(lote.validade)} />
        <Dado rotulo="Quantidade conhecida" valor={lote.quantidade ?? 'indisponível'} />
        <Dado
          rotulo="Origem"
          valor={lote.origem === 'manual' ? 'Lançamento manual' : origem(lote.origem)}
        />
        <Dado rotulo="Nota de entrada" valor={lote.notaEntrada ?? '—'} />
        <Dado rotulo="Data de entrada" valor={lote.dataEntrada ? data(lote.dataEntrada) : '—'} />
        <Dado rotulo="Atualizado" valor={dataHoraCurta(lote.atualizadoEm)} />
      </div>
      <div className="ac-rotulo-mono" style={{ marginTop: 14 }}>
        HISTÓRICO DO LOTE
      </div>
      {lote.eventos.map((e, i) => (
        <div key={i} className="ac-historico">
          <span className="ac-historico-hora">{diaMes(e.criadoEm.slice(0, 10))}</span>
          <div style={{ flex: 1 }}>
            {descreverEvento(e)}
            {e.motivo && <div className="pv-motivo">Motivo: {e.motivo}</div>}
          </div>
        </div>
      ))}
      <div className="ac-pe-modal">
        Vendas antigas deste lote guardam o próprio retrato: alterar a validade hoje não reescreve a
        evidência de uma venda de ontem.
      </div>
      {configura && lote.ativo && (
        <button type="button" className="ac-botao" style={{ marginTop: 10 }} onClick={aoCorrigir}>
          Registrar correção
        </button>
      )}
    </div>
  );
}

function Dado({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="ac-linha-dado">
      <span>{rotulo}</span>
      <span>{valor}</span>
    </div>
  );
}

function descreverEvento(e: EventoDoLote): string {
  const quem = e.ator ? ` por ${e.ator}` : '';
  const d = e.depois ?? {};
  const a = e.antes ?? {};
  const txt = (v: unknown) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '—');
  switch (e.tipo) {
    case 'entrada':
      return `Entrada${d['nota_entrada'] ? ` pela ${txt(d['nota_entrada'])}` : ''} · ${txt(d['quantidade'])} unidades${quem}${
        d['codigo_informado'] && d['codigo_informado'] !== d['codigo']
          ? ` (digitado "${txt(d['codigo_informado'])}")`
          : ''
      }`;
    case 'soma_quantidade':
      return `Somadas ${txt(d['somado'])} unidades · ${txt(a['quantidade'])} → ${txt(d['quantidade'])}${quem}`;
    case 'ajuste_quantidade':
      return `Quantidade alterada ${txt(a['quantidade'])} → ${txt(d['quantidade'])}${quem}`;
    case 'correcao_validade':
      return `Validade alterada ${data(a['validade'] as string | null)} → ${data(d['validade'] as string | null)}${quem}`;
    case 'expirado':
      return 'Saiu do catálogo na virada do mês da validade (automático)';
    case 'desativado':
      return `Desativado${quem}`;
    default:
      return e.tipo;
  }
}

function Lancamento({
  sessao,
  productId,
  aoCancelar,
  aoSalvar,
  aoCorrigirExistente,
}: {
  sessao: Sessao;
  productId: string;
  aoCancelar: () => void;
  aoSalvar: (loteId: string) => void;
  aoCorrigirExistente: (loteId: string) => void;
}) {
  const [codigo, setCodigo] = useState('');
  const [validade, setValidade] = useState('');
  const [quantidade, setQuantidade] = useState('');
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [existente, setExistente] = useState<{
    lote: LoteExistente;
    mesmaValidade: boolean;
  } | null>(null);

  async function enviar(e: FormEvent | null, somar = false) {
    e?.preventDefault();
    if (enviando) return;
    const q = quantidade.trim().replace(',', '.');
    if (!/^\d+(\.\d{1,4})?$/.test(q) || Number(q) <= 0)
      return setErro('Quantidade precisa ser maior que zero.');
    setEnviando(true);
    setErro(null);
    const r = await lancarLote(sessao, productId, {
      codigo,
      validade: validade || null,
      quantidade: q,
      notaEntrada: nota.trim() || null,
      somarAoExistente: somar,
    });
    setEnviando(false);
    if (r.ok) return aoSalvar(r.dados.loteId);
    if (r.falha.tipo === 'ja_cadastrado')
      return setExistente({ lote: r.falha.existente, mesmaValidade: r.falha.mesmaValidade });
    setErro(mensagemDeFalha(r.falha));
  }

  return (
    <form className="ac-secao pv-lancar" onSubmit={(e) => void enviar(e)} noValidate>
      <div className="pv-detalhe-titulo">Lançar lote</div>
      <div className="ac-subtitulo" style={{ marginBottom: 12 }}>
        O servidor valida o código e a validade · a data e a hora oficiais são as do servidor
      </div>
      <div className="pv-form-grade">
        <label>
          <span className="mc-rotulo">Número do lote</span>
          <input
            className="mc-entrada pv-entrada"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            placeholder="L1187"
            autoCapitalize="characters"
            spellCheck={false}
          />
          <span className="pv-dica">{TEXTO_DA_REGRA_DO_L}</span>
        </label>
        <label>
          <span className="mc-rotulo">Validade</span>
          <input
            className="mc-entrada pv-entrada"
            type="date"
            value={validade}
            onChange={(e) => setValidade(e.target.value)}
          />
          <span className="pv-dica">
            Sem data na embalagem? Deixe em branco: fica "não informada", nunca uma data inventada.
          </span>
        </label>
        <label>
          <span className="mc-rotulo">Quantidade</span>
          <input
            className="mc-entrada pv-entrada"
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            inputMode="numeric"
            placeholder="3"
          />
        </label>
        <label>
          <span className="mc-rotulo">Nota de entrada (opcional)</span>
          <input
            className="mc-entrada pv-entrada"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="NF 44182"
          />
        </label>
      </div>

      {existente && (
        <div className="pv-existente" role="alert">
          <div className="pv-existente-titulo">
            {existente.mesmaValidade
              ? 'Este lote já está cadastrado'
              : 'Este lote já existe com outra validade'}
          </div>
          <div className="pv-existente-texto">
            Validade atual <b>{data(existente.lote.validade)}</b> · quantidade conhecida{' '}
            <b>{existente.lote.quantidade ?? 'indisponível'}</b> · origem{' '}
            <b>{origem(existente.lote.origem)}</b>.
            {!existente.mesmaValidade &&
              ' A tela não escolhe uma das datas: corrija o lote existente com o motivo.'}
          </div>
          <div className="pv-existente-acoes">
            {existente.mesmaValidade && (
              <button
                type="button"
                className="ac-botao ac-botao-primario"
                disabled={enviando}
                onClick={() => void enviar(null, true)}
              >
                Somar {quantidade || '0'} ao lote
              </button>
            )}
            <button
              type="button"
              className="ac-botao"
              onClick={() => aoCorrigirExistente(existente.lote.id)}
            >
              Registrar correção
            </button>
            <button type="button" className="ac-botao" onClick={() => setExistente(null)}>
              Cancelar
            </button>
          </div>
        </div>
      )}
      {erro && (
        <div className="mc-erro" role="alert">
          <Icone nome="warn" tamanho={14} traco={2} />
          {erro}
        </div>
      )}
      {!existente && (
        <div className="pv-form-acoes">
          <button
            type="submit"
            className="ac-botao ac-botao-primario pd-salvar"
            disabled={enviando}
          >
            {enviando ? 'Lançando…' : 'Lançar lote'}
          </button>
          <button type="button" className="ac-botao pd-descartar" onClick={aoCancelar}>
            Cancelar
          </button>
        </div>
      )}
    </form>
  );
}

function Correcao({
  sessao,
  lote,
  aoCancelar,
  aoSalvar,
  aoRecarregar,
}: {
  sessao: Sessao;
  lote: ProdutoComLotes['lotes'][number];
  aoCancelar: () => void;
  aoSalvar: () => void;
  aoRecarregar: () => void;
}) {
  const [validade, setValidade] = useState(lote.validade ?? '');
  const [quantidade, setQuantidade] = useState(lote.quantidade ?? '');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<{ texto: string; conflito: boolean } | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (enviando) return;
    const mudaValidade = (validade || null) !== lote.validade;
    const q = quantidade.trim().replace(',', '.');
    const mudaQtd = q !== '' && q !== lote.quantidade;
    if (!mudaValidade && !mudaQtd)
      return setErro({ texto: 'Nada mudou: altere a validade ou a quantidade.', conflito: false });
    if (!motivo.trim()) return setErro({ texto: 'Diga o motivo da correção.', conflito: false });
    setEnviando(true);
    setErro(null);
    const r = await corrigirLote(sessao, lote.id, {
      versaoEsperada: lote.versao,
      ...(mudaValidade ? { validade: validade || null } : {}),
      ...(mudaQtd ? { quantidade: q } : {}),
      motivo: motivo.trim(),
    });
    setEnviando(false);
    if (r.ok) return aoSalvar();
    setErro({ texto: mensagemDeFalha(comoFalha(r.falha)), conflito: r.falha.tipo === 'conflito' });
  }

  return (
    <form className="ac-secao pv-lancar" onSubmit={(e) => void enviar(e)} noValidate>
      <div className="pv-detalhe-titulo">Registrar correção · lote {lote.codigo}</div>
      <div className="ac-subtitulo" style={{ marginBottom: 12 }}>
        O valor anterior continua na linha do tempo, com você como autor e o motivo.
      </div>
      <div className="pv-form-grade">
        <label>
          <span className="mc-rotulo">Validade</span>
          <input
            className="mc-entrada pv-entrada"
            type="date"
            value={validade}
            onChange={(e) => setValidade(e.target.value)}
          />
        </label>
        <label>
          <span className="mc-rotulo">Quantidade</span>
          <input
            className="mc-entrada pv-entrada"
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            inputMode="numeric"
          />
        </label>
      </div>
      <label style={{ display: 'block', marginTop: 12 }}>
        <span className="mc-rotulo">Motivo</span>
        <textarea
          className="ac-texto-area"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          placeholder="Ex.: conferência física da embalagem"
          maxLength={500}
        />
      </label>
      {erro && (
        <div className={erro.conflito ? 'pv-existente' : 'mc-erro'} role="alert">
          {erro.conflito ? (
            <>
              <div className="pv-existente-titulo">Este lote foi atualizado por outro usuário</div>
              <button type="button" className="ac-botao" onClick={aoRecarregar}>
                Recarregar
              </button>
            </>
          ) : (
            <>
              <Icone nome="warn" tamanho={14} traco={2} />
              {erro.texto}
            </>
          )}
        </div>
      )}
      <div className="pv-form-acoes">
        <button type="submit" className="ac-botao ac-botao-primario pd-salvar" disabled={enviando}>
          {enviando ? 'Salvando…' : 'Salvar correção'}
        </button>
        <button type="button" className="ac-botao pd-descartar" onClick={aoCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
